import { forwardRef, useEffect, useRef, useState } from 'react';

import { Portal } from '../portal/portal';

import type { ComboboxContentProps } from './combobox.types';

export const ComboboxContent = forwardRef<HTMLDivElement, ComboboxContentProps>(
  (
    {
      isOpen,
      triggerRef,
      children,
      portalTarget,
      zIndex = 1500, // popover level - must be above modal (1400)
      size = 'md',
      closeOnOutsideClick = true,
      onClose,
      id,
      role = 'listbox',
      ...ariaProps
    },
    ref
  ) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [positionKey, setPositionKey] = useState(0);

    // Trigger positioning when dropdown opens
    useEffect(() => {
      if (isOpen) {
        setPositionKey((prev) => prev + 1);
      }
    }, [isOpen]);

    // Position dropdown below trigger
    useEffect(() => {
      if (!isOpen || !triggerRef.current || !contentRef.current) return;

      const trigger = triggerRef.current;
      const content = contentRef.current;

      const updatePosition = () => {
        // Get fresh measurements
        const triggerRect = trigger.getBoundingClientRect();

        // Use viewport dimensions (not documentElement) for accurate positioning
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        const spaceBelow = viewportHeight - triggerRect.bottom;
        const spaceAbove = triggerRect.top;

        // Determine placement (prefer below, but use above if not enough space)
        const placeAbove = spaceBelow < 300 && spaceAbove > spaceBelow;

        // Position vertically
        if (placeAbove) {
          content.style.bottom = `${viewportHeight - triggerRect.top + 4}px`;
          content.style.top = 'auto';
        } else {
          content.style.top = `${triggerRect.bottom + 4}px`;
          content.style.bottom = 'auto';
        }

        // Position horizontally (centered under trigger)
        content.style.left = `${triggerRect.left}px`;
        content.style.minWidth = `${triggerRect.width}px`;

        // Ensure dropdown doesn't overflow viewport horizontally
        const contentWidth = content.offsetWidth || parseFloat(content.style.minWidth);
        if (triggerRect.left + contentWidth > viewportWidth) {
          content.style.left = `${Math.max(0, viewportWidth - contentWidth - 8)}px`;
        }

        // Now that positioning is complete, make visible
        content.style.visibility = 'visible';
        content.style.opacity = '1';
      };

      // Use requestAnimationFrame to ensure element is fully rendered and measured
      let rafId: number;
      const scheduleUpdate = () => {
        rafId = requestAnimationFrame(() => {
          updatePosition();
        });
      };

      scheduleUpdate();

      // Update position on scroll/resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }, [isOpen, triggerRef, positionKey]);

    // Handle outside clicks
    useEffect(() => {
      if (!isOpen || !closeOnOutsideClick) return;

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (
          contentRef.current &&
          !contentRef.current.contains(target) &&
          triggerRef.current &&
          !triggerRef.current.contains(target)
        ) {
          onClose?.();
        }
      };

      // Use capture phase to handle clicks before they bubble
      document.addEventListener('mousedown', handleClickOutside, true);
      return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, [isOpen, closeOnOutsideClick, onClose, triggerRef]);

    // Handle escape key
    useEffect(() => {
      if (!isOpen) return;

      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          onClose?.();
          // Return focus to trigger
          if (triggerRef.current) {
            triggerRef.current.focus();
          }
        }
      };

      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose, triggerRef]);

    if (!isOpen) return null;

    const content = (
      <div
        ref={(node) => {
          contentRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        style={{
          position: 'fixed',
          zIndex,
          top: 0, // Start at 0, will be positioned by useEffect
          left: 0, // Start at 0, will be positioned by useEffect
          borderRadius: '6px',
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
          minWidth: size === 'sm' ? '200px' : size === 'lg' ? '300px' : '250px',
          maxWidth: size === 'sm' ? '300px' : size === 'lg' ? '500px' : '400px',
          visibility: 'hidden', // Hidden until positioned
          opacity: '0',
          pointerEvents: 'auto', // Ensure clicks work inside modal dialogs
        }}
        id={id}
        role={role}
        {...ariaProps}
      >
        {children}
      </div>
    );

    // Use custom portal target if provided
    if (portalTarget) {
      return <Portal containerRef={{ current: portalTarget }}>{content}</Portal>;
    }

    return <Portal>{content}</Portal>;
  }
);

ComboboxContent.displayName = 'ComboboxContent';
